import React from 'react';

/**
 * Global safety net. Any uncaught render error would otherwise unmount React
 * and leave a blank white page. This shows a graceful fallback instead and
 * lets the customer recover (reload / go home) without losing the session.
 * `resetKey` (the current pathname) clears the error on navigation.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Surface for debugging; never crash the whole app.
    // eslint-disable-next-line no-console
    console.error('Render error caught by ErrorBoundary:', error, info);
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center text-center px-6"
          style={{ paddingTop: 'var(--nav-h, 112px)' }} data-testid="error-boundary">
          <p className="overline tracking-[0.3em] text-[#B08D57] mb-3">Something went wrong</p>
          <h1 className="text-3xl mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>We hit a snag</h1>
          <p className="text-[#595956] mb-8 max-w-md">
            This page ran into a problem. Your cart and account are safe — please reload or head back home.
          </p>
          <div className="flex gap-3">
            <button onClick={() => window.location.reload()} className="btn-luxe" data-testid="error-reload">Reload</button>
            <a href="/" className="btn-luxe-outline" data-testid="error-home">Return Home</a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
